import {lazy, Suspense} from 'react'

import {Box, Grid} from '@primer/react-brand/esm'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

import {ContentfulFaqGroup} from '@github-ui/swp-core/components/contentful/ContentfulFaqGroup'
import {ContentfulInlineFootnotesList} from '@github-ui/swp-core/components/contentful/ContentfulInlineFootnotesList'
import {ContentfulSubnav} from '@github-ui/swp-core/components/contentful/ContentfulSubnav'
import {useNinetailedConfig} from '@github-ui/swp-core/contexts/NinetailedConfigContext'
import type {NinetailedExperienceEntry} from '@github-ui/swp-core/lib/utils/contentful-ninetailed'

import {BrandPageContent} from '../../../../brand/components/BrandPage'
import type {GenericSection} from '../../../../brand/lib/types/contentful'

import {PlanTypeContextProvider} from '../_context/PlanTypeContext'

import {CompareTable} from './CompareTable'
import {CopilotPlansExperimentSkeleton} from './CopilotPlansExperimentSkeleton'
import {copilotPlansPage} from './content'
import {CtaSection} from './CtaSection'
import {HeroSection} from './HeroSection'
import type {HeroSectionContent} from './HeroSection/content'

// The Ninetailed SDK graph is browser-only: `diary` reads `process.env.DEBUG` and the
// `@analytics` packages read `window.__global__` while their modules evaluate. Importing this
// statically crashes the Alloy SSR sandbox, so the async chunk is what keeps it off the server.
const NinetailedPlansHeroSection = lazy(() => import('./NinetailedPlansHeroSection'))

type PlansHeroSectionProps = {
  content: HeroSectionContent
  contentfulContent: GenericSection
}

export function PlansHeroSection({content, contentfulContent}: PlansHeroSectionProps) {
  const ninetailedConfig = useNinetailedConfig()
  const baseline = <HeroSection content={content} />
  const rawExperiences: NinetailedExperienceEntry[] =
    isFeatureEnabled('landing_pages_ninetailed') && Array.isArray(contentfulContent.fields.nt_experiences)
      ? contentfulContent.fields.nt_experiences
      : []

  if (ninetailedConfig === null || rawExperiences.length === 0) {
    return baseline
  }

  return (
    <ErrorBoundary boundaryName="ninetailed-plans-hero-section" fallback={baseline}>
      <Suspense fallback={<CopilotPlansExperimentSkeleton />}>
        <NinetailedPlansHeroSection
          contentfulContent={contentfulContent}
          ninetailedConfig={ninetailedConfig}
          rawExperiences={rawExperiences}
        />
      </Suspense>
    </ErrorBoundary>
  )
}

export default function FeaturesCopilotPlansIndex() {
  return (
    <BrandPageContent
      definition={copilotPlansPage}
      colorMode="dark"
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      className="lp-Copilot position-relative"
      withFootnotes
      wrapTheme={theme => <PlanTypeContextProvider>{theme}</PlanTypeContextProvider>}
    >
      {({comparison, cta, faq, hero, subnav}) => (
        <>
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <Box className="lp-SubNav-spacer" />

          {subnav ? (
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            <ContentfulSubnav component={subnav} className="lp-Hero-subnav lp-Hero-subnav--highContrast" />
          ) : null}

          {hero ? <PlansHeroSection contentfulContent={hero.section} content={hero.content} /> : null}

          {comparison ? <CompareTable content={comparison} /> : null}

          {cta ? <CtaSection content={cta} /> : null}

          {faq ? (
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            <section id="faq" className="lp-Section tmp-pb-8 tmp-pb-md-12 tmp-pt-8 tmp-pt-md-12">
              <Grid>
                {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
                <Grid.Column span={12} className="lp-FAQs">
                  <ContentfulFaqGroup component={faq} />
                </Grid.Column>
              </Grid>
            </section>
          ) : null}

          <Grid>
            <Grid.Column span={12}>
              <ContentfulInlineFootnotesList />
            </Grid.Column>
          </Grid>
        </>
      )}
    </BrandPageContent>
  )
}
